import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    ClipboardCheck, ArrowRight, MapPinned, Loader2, MapPin, Calendar,
    List, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, ShieldCheck,
    FileDown, Building2, Clock, X, Camera, Pencil, ExternalLink, Lock,
} from 'lucide-react';
import InstallPWAButton from '@/components/InstallPWAButton';
import { getDisplayTitle } from '@/lib/displayTitle';

const ACCENT = '#00875A';
const SIGNAL_GREEN = '#00875A';

const STATUS_META = {
    pending: { label: 'Pendiente', color: '#6B7280', bg: '#F3F4F6' },
    assigned: { label: 'Asignada', color: '#7C6BEF', bg: 'rgba(124,107,239,0.10)' },
    in_progress: { label: 'En curso', color: '#F59E0B', bg: 'rgba(245,158,11,0.10)' },
    in_review: { label: 'En revisión', color: '#0EA5E9', bg: 'rgba(14,165,233,0.10)' },
    approved: { label: 'Aprobada', color: '#00875A', bg: 'rgba(0,135,90,0.10)' },
    rejected: { label: 'Rechazada', color: '#EF4444', bg: 'rgba(239,68,68,0.10)' },
};

export default function InspectorDashboard() {
    const { user, setUser } = useAuth();
    const [schedule, setSchedule] = useState([]);
    const [verWorks, setVerWorks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [geoLoading, setGeoLoading] = useState(false);
    const [lastLocation, setLastLocation] = useState(
        user?.latitude && user?.longitude ? { lat: user.latitude, lng: user.longitude } : null,
    );
    const [scheduleView, setScheduleView] = useState('list'); // 'list' | 'calendar'
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterMonth, setFilterMonth] = useState(() => new Date().toISOString().slice(0, 7)); // YYYY-MM
    const [selectedWork, setSelectedWork] = useState(null);
    const [distintivos, setDistintivos] = useState([]);
    const distLookup = useMemo(() => Object.fromEntries(distintivos.map((d) => [d.id, d])), [distintivos]);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [s, v] = await Promise.all([
                api.get('/inspector/schedule'),
                api.get('/inspector/trabajos-verificacion'),
            ]);
            setSchedule(s.data || []);
            setVerWorks(v.data || []);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'No se pudo cargar el dashboard'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, []);
    useEffect(() => { api.get('/public/distintivos').then((r) => setDistintivos(r.data || [])).catch(() => setDistintivos([])); }, []);

    const updateLocation = () => {
        if (!navigator.geolocation) return toast.error('Tu navegador no soporta geolocalización');
        setGeoLoading(true);
        navigator.geolocation.getCurrentPosition(async (pos) => {
            const lat = parseFloat(pos.coords.latitude.toFixed(6));
            const lng = parseFloat(pos.coords.longitude.toFixed(6));
            try {
                await api.patch('/inspector/me/location', { latitude: lat, longitude: lng });
                setLastLocation({ lat, lng });
                if (setUser && user) setUser({ ...user, latitude: lat, longitude: lng });
                toast.success('Ubicación actualizada');
            } catch { toast.error('No se pudo guardar la ubicación'); }
            finally { setGeoLoading(false); }
        }, (err) => { toast.error(`No se pudo obtener ubicación: ${err.message}`); setGeoLoading(false); },
            { enableHighAccuracy: true, timeout: 10000 });
    };

    const filteredSchedule = useMemo(() => {
        return schedule.filter((r) => {
            if (filterStatus !== 'all' && r.status !== filterStatus) return false;
            if (filterMonth && r.scheduled_date && !r.scheduled_date.startsWith(filterMonth)) return false;
            return true;
        });
    }, [schedule, filterStatus, filterMonth]);

    const kpis = useMemo(() => ({
        total: schedule.length,
        thisMonth: schedule.filter((r) => r.scheduled_date && r.scheduled_date.startsWith(filterMonth)).length,
        inReview: verWorks.length,
        approved: schedule.filter((r) => r.status === 'approved').length,
    }), [schedule, verWorks, filterMonth]);

    return (
        <DashboardLayout role="inspector">
            {/* Hero */}
            <div className="mb-6">
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: ACCENT }}>◆ Panel Auditor</div>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <h1 className="text-3xl md:text-4xl font-display font-black">Hola, {user?.name?.split(' ')[0] || 'Auditor'}</h1>
                    <div className="flex items-center gap-2">
                        <InstallPWAButton style={{ color: ACCENT }} />
                        <button onClick={loadAll} data-testid="inspector-refresh"
                            className="border-2 px-3 py-2 text-sm font-bold inline-flex items-center gap-1 hover:bg-black/5"
                            style={{ borderColor: 'var(--border)' }}>
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Actualizar'}
                        </button>
                    </div>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <KpiCard label="Total auditorías" value={kpis.total} Icon={ClipboardCheck} color={ACCENT} />
                <KpiCard label="Este mes" value={kpis.thisMonth} Icon={Calendar} color="#7C6BEF" />
                <KpiCard label="En verificación" value={kpis.inReview} Icon={ShieldCheck} color="#0EA5E9" />
                <KpiCard label="Aprobadas" value={kpis.approved} Icon={CheckCircle2} color="#00875A" />
            </div>

            {/* Cerrojo informativo — el auditor no crea nada, solo ejecuta */}
            <div className="mb-6 p-3 border-l-4 flex items-start gap-3 text-xs"
                style={{ background: 'rgba(107,114,128,0.06)', borderColor: '#6B7280' }}
                data-testid="inspector-lock-banner">
                <Lock className="w-4 h-4 shrink-0 mt-0.5 opacity-70" />
                <div className="opacity-80">
                    <strong>Solo el administrador asigna nuevas auditorías y define los checklists.</strong>
                    {' '}Aquí ejecutas las auditorías que ya te fueron asignadas y respondes el checklist definido por el admin para el rubro del cliente.
                </div>
            </div>

            {/* Mi ubicación */}
            <section className="mb-6 border p-4 flex flex-col md:flex-row md:items-center justify-between gap-3"
                style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}>
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: 'rgba(0,135,90,0.10)', color: ACCENT }}>
                        <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="font-display font-black">Mi ubicación actual</div>
                        <div className="text-xs opacity-70 mt-0.5">
                            {lastLocation ? (
                                <>📍 {lastLocation.lat}, {lastLocation.lng} · {' '}
                                    <a href={`https://www.google.com/maps?q=${lastLocation.lat},${lastLocation.lng}`}
                                        target="_blank" rel="noreferrer" className="underline" style={{ color: ACCENT }}>
                                        Ver en Google Maps ↗
                                    </a></>
                            ) : ('Aún no has compartido tu ubicación con el administrador')}
                        </div>
                    </div>
                </div>
                <button onClick={updateLocation} disabled={geoLoading} data-testid="update-my-location"
                    className="text-white px-4 py-2.5 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50 shrink-0"
                    style={{ background: ACCENT }}>
                    {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPinned className="w-4 h-4" />}
                    {geoLoading ? 'Obteniendo...' : 'Actualizar ubicación'}
                </button>
            </section>

            {/* Mi cronograma */}
            <section className="mb-8">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                    <div>
                        <h2 className="font-display font-black text-2xl">Mi cronograma</h2>
                        <div className="text-xs opacity-60">Todas mis auditorías planeadas · {filteredSchedule.length} en el filtro actual</div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}
                            data-testid="schedule-month-filter"
                            className="px-3 py-2 border text-sm bg-transparent" style={{ borderColor: 'var(--border)' }} />
                        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                            data-testid="schedule-status-filter"
                            className="px-3 py-2 border text-sm bg-transparent" style={{ borderColor: 'var(--border)' }}>
                            <option value="all">Todos los estados</option>
                            <option value="assigned">Asignadas</option>
                            <option value="in_progress">En curso</option>
                            <option value="in_review">En revisión</option>
                            <option value="approved">Aprobadas</option>
                            <option value="rejected">Rechazadas</option>
                        </select>
                        <div className="inline-flex border-2" style={{ borderColor: 'var(--border)' }}>
                            <button onClick={() => setScheduleView('list')} data-testid="view-list"
                                className={`px-3 py-2 text-sm font-bold inline-flex items-center gap-1 ${scheduleView === 'list' ? 'text-white' : ''}`}
                                style={{ background: scheduleView === 'list' ? ACCENT : 'transparent' }}>
                                <List className="w-4 h-4" /> Lista
                            </button>
                            <button onClick={() => setScheduleView('calendar')} data-testid="view-calendar"
                                className={`px-3 py-2 text-sm font-bold inline-flex items-center gap-1 ${scheduleView === 'calendar' ? 'text-white' : ''}`}
                                style={{ background: scheduleView === 'calendar' ? ACCENT : 'transparent' }}>
                                <Calendar className="w-4 h-4" /> Calendario
                            </button>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="p-12 text-center opacity-60"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                ) : scheduleView === 'list' ? (
                    <ScheduleList items={filteredSchedule} distLookup={distLookup} />
                ) : (
                    <ScheduleCalendar items={schedule} month={filterMonth} onMonthChange={setFilterMonth} onSelect={(r) => setSelectedWork({ type: 'schedule', data: r })} />
                )}
            </section>

            {/* Trabajos en verificación */}
            <section>
                <div className="mb-3">
                    <h2 className="font-display font-black text-2xl">Trabajos en verificación</h2>
                    <div className="text-xs opacity-60">Auditorías que enviaste al administrador. Aquí revisas checklist, fotos IA y fundamentos.</div>
                </div>
                {loading ? (
                    <div className="p-12 text-center opacity-60"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                ) : verWorks.length === 0 ? (
                    <div className="p-12 text-center border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}>
                        <ShieldCheck className="w-10 h-10 mx-auto opacity-40 mb-2" />
                        <div className="opacity-70">Sin trabajos esperando visto bueno del administrador.</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="verification-list">
                        {verWorks.map((w) => (
                            <VerificationCard key={w.id} work={w} distLookup={distLookup} onOpen={() => setSelectedWork({ type: 'verification', data: w })} />
                        ))}
                    </div>
                )}
            </section>

            {selectedWork?.type === 'verification' && (
                <VerificationDetailModal work={selectedWork.data} distLookup={distLookup} onClose={() => setSelectedWork(null)} onReload={loadAll} />
            )}
        </DashboardLayout>
    );
}

function KpiCard({ label, value, Icon, color }) {
    return (
        <div className="p-4 border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}>
            <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">{label}</div>
                <Icon className="w-4 h-4" style={{ color }} />
            </div>
            <div className="text-3xl font-display font-black" style={{ color }}>{value}</div>
        </div>
    );
}

function ScheduleList({ items, distLookup }) {
    if (!items.length) return (
        <div className="p-12 text-center border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}>
            <Calendar className="w-10 h-10 mx-auto opacity-40 mb-2" />
            <div className="opacity-70">Sin auditorías en este filtro.</div>
        </div>
    );
    return (
        <div className="space-y-2" data-testid="schedule-list">
            {items.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.pending;
                return (
                    <div key={r.id} className="border p-4 flex flex-col md:flex-row md:items-center gap-3"
                        data-testid={`schedule-row-${r.id}`}
                        style={{ background: 'var(--card-bg)', borderColor: 'var(--border)', borderLeftWidth: 4, borderLeftColor: meta.color }}>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5"
                                    style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                                <span className="text-[10px] uppercase tracking-widest font-bold opacity-80">{getDisplayTitle(r, distLookup).title}</span>
                            </div>
                            <div className="font-display font-black text-lg truncate">{r.client?.name || '—'}</div>
                            <div className="text-xs opacity-70 mt-0.5 flex items-center gap-3 flex-wrap">
                                <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {r.scheduled_date || 'Sin fecha'} {r.scheduled_time || ''}</span>
                                {r.client?.industry && <span>· {r.client.industry}</span>}
                                {r.client?.rfc && <span>· RFC: {r.client.rfc}</span>}
                                {r.client?.city && <span>· {r.client.city}</span>}
                            </div>
                        </div>
                        {r.status === 'assigned' || r.status === 'in_progress' ? (
                            <Link to={`/inspector/inspections/${r.id}/form`}
                                data-testid={`open-form-${r.id}`}
                                className="text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-2 shrink-0"
                                style={{ background: ACCENT }}>
                                Abrir formulario <ArrowRight className="w-4 h-4" />
                            </Link>
                        ) : (
                            <Link to={`/inspector/inspections/${r.id}/form`}
                                className="border-2 px-4 py-2 text-sm font-bold inline-flex items-center gap-2 shrink-0"
                                style={{ borderColor: 'var(--border)' }}>
                                Ver detalles <ArrowRight className="w-4 h-4" />
                            </Link>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function ScheduleCalendar({ items, month, onMonthChange, onSelect }) {
    const [y, m] = month.split('-').map(Number);
    const first = new Date(y, m - 1, 1);
    const daysInMonth = new Date(y, m, 0).getDate();
    const startOffset = (first.getDay() + 6) % 7; // Lunes = 0

    const byDay = useMemo(() => {
        const map = {};
        items.forEach((r) => {
            if (!r.scheduled_date) return;
            if (!r.scheduled_date.startsWith(month)) return;
            const d = parseInt(r.scheduled_date.slice(8, 10), 10);
            (map[d] = map[d] || []).push(r);
        });
        return map;
    }, [items, month]);

    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    const changeMonth = (delta) => {
        const nd = new Date(y, m - 1 + delta, 1);
        onMonthChange(`${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, '0')}`);
    };

    return (
        <div className="border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }} data-testid="schedule-calendar">
            <div className="p-3 flex items-center justify-between border-b" style={{ borderColor: 'var(--border)' }}>
                <button onClick={() => changeMonth(-1)} data-testid="cal-prev" className="p-2 hover:bg-black/5"><ChevronLeft className="w-4 h-4" /></button>
                <div className="font-display font-black">{first.toLocaleDateString('es', { month: 'long', year: 'numeric' })}</div>
                <button onClick={() => changeMonth(1)} data-testid="cal-next" className="p-2 hover:bg-black/5"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-7 text-[10px] uppercase tracking-widest font-bold opacity-60 border-b" style={{ borderColor: 'var(--border)' }}>
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d) => <div key={d} className="p-2 text-center">{d}</div>)}
            </div>
            <div className="grid grid-cols-7">
                {cells.map((d, i) => (
                    <div key={i} className="min-h-[90px] border-r border-b p-1.5 flex flex-col gap-1" style={{ borderColor: 'var(--border)' }}>
                        {d && (
                            <>
                                <div className="text-xs opacity-60 font-bold">{d}</div>
                                {(byDay[d] || []).slice(0, 3).map((r) => {
                                    const meta = STATUS_META[r.status] || STATUS_META.pending;
                                    return (
                                        <button key={r.id} onClick={() => onSelect(r)}
                                            className="text-left text-[10px] px-1.5 py-1 truncate hover:opacity-80"
                                            style={{ background: meta.bg, color: meta.color, borderLeft: `2px solid ${meta.color}` }}>
                                            {r.scheduled_time ? `${r.scheduled_time} · ` : ''}{r.client?.name || '—'}
                                        </button>
                                    );
                                })}
                                {(byDay[d] || []).length > 3 && (
                                    <div className="text-[9px] opacity-60 italic">+{byDay[d].length - 3} más</div>
                                )}
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function VerificationCard({ work, distLookup, onOpen }) {
    return (
        <button onClick={onOpen}
            data-testid={`verification-card-${work.id}`}
            className="text-left border p-4 hover:shadow-lg transition-shadow"
            style={{ background: 'var(--card-bg)', borderColor: 'var(--border)', borderLeftWidth: 4, borderLeftColor: '#0EA5E9' }}>
            <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5"
                    style={{ background: 'rgba(14,165,233,0.10)', color: '#0EA5E9' }}>En revisión</span>
                <span className="text-[10px] uppercase tracking-widest font-bold opacity-80">{getDisplayTitle(work, distLookup).title}</span>
            </div>
            <div className="font-display font-black text-lg mb-1">{work.client?.name}</div>
            <div className="text-xs opacity-70 flex items-center gap-3 flex-wrap mb-2">
                <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {work.client?.industry || '—'}</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {work.scheduled_date || '—'}</span>
            </div>
            <div className="flex items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1"><Camera className="w-3 h-3" /> {work.photos_summary?.total} evidencias</span>
                {work.photos_summary?.critical > 0 && (
                    <span className="inline-flex items-center gap-1 font-bold" style={{ color: '#EF4444' }}>
                        <AlertTriangle className="w-3 h-3" /> {work.photos_summary.critical} críticos
                    </span>
                )}
                {work.photos_summary?.warning > 0 && (
                    <span className="inline-flex items-center gap-1 font-bold" style={{ color: '#F59E0B' }}>
                        {work.photos_summary.warning} warning
                    </span>
                )}
                <span className="ml-auto text-[10px] uppercase tracking-widest font-bold opacity-60">Ver detalle →</span>
            </div>
        </button>
    );
}

function VerificationDetailModal({ work, distLookup, onClose, onReload }) {
    const [downloading, setDownloading] = useState(false);
    const { title: distTitle } = getDisplayTitle(work, distLookup);

    const downloadCertificate = async () => {
        setDownloading(true);
        try {
            const r = await api.post(`/certifications/${work.id}/certificate`, {}, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `distintivo_${distTitle}.pdf`.replace(/[ :]/g, '_');
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'No se pudo generar el distintivo (solo aprobadas)'));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}
            data-testid="verification-detail-modal">
            <div className="max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border"
                style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}
                onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b flex items-center justify-between shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Trabajo en verificación</div>
                        <div className="font-display font-black text-2xl">{work.client?.name}</div>
                        <div className="text-xs opacity-70">{distTitle}</div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-black/5"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <Kpi label="RFC" value={work.client?.rfc || '—'} />
                        <Kpi label="Industria" value={work.client?.industry || '—'} />
                        <Kpi label="Score" value={work.score_data?.score != null ? `${work.score_data.score.toFixed(1)}%` : '—'} />
                        <Kpi label="Umbral norma" value={`${work.standard?.approval_threshold || 80}%`} />
                    </div>

                    {/* Fotos con IA + observaciones (solo lectura, ya en review) */}
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-2 flex items-center gap-2">
                            <Camera className="w-3 h-3" /> Evidencias fotográficas ({work.photos?.length || 0})
                            <span className="opacity-80">
                                · {work.photos_summary?.critical || 0} críticos · {work.photos_summary?.warning || 0} warning · {work.photos_summary?.ok || 0} ok
                            </span>
                        </div>
                        {!work.photos?.length ? (
                            <div className="p-6 text-center text-sm opacity-60 border" style={{ borderColor: 'var(--border)' }}>
                                Sin evidencias fotográficas.
                            </div>
                        ) : (
                            <ul className="space-y-2">
                                {work.photos.map((p) => (
                                    <PhotoItem key={p.id} photo={p} readOnly onEdited={onReload} />
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="p-3 border" style={{ borderColor: 'var(--border)', background: 'rgba(0,135,90,0.03)' }}>
                        <div className="text-xs">
                            <strong>Estado:</strong> Este trabajo está en revisión del administrador. Las observaciones ya no son editables.
                            {work.score_data?.score != null && work.score_data.score >= (work.standard?.approval_threshold || 80)
                                ? ' Cumple el umbral de aprobación.'
                                : ' Está por debajo del umbral: el admin decidirá.'}
                        </div>
                    </div>

                    <div className="flex gap-2 pt-2 flex-wrap">
                        <button onClick={downloadCertificate} disabled={downloading}
                            data-testid="download-certificate-btn"
                            className="text-white px-4 py-2 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                            style={{ background: ACCENT }}
                            title="Solo disponible tras la aprobación final del admin">
                            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                            Descargar distintivo (si aprobada)
                        </button>
                        <Link to={`/inspector/inspections/${work.id}/form`}
                            className="border-2 px-4 py-2 font-bold inline-flex items-center justify-center gap-2"
                            style={{ borderColor: 'var(--border)' }}>
                            Ver formulario completo <ExternalLink className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PhotoItem({ photo, readOnly, onEdited }) {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(photo.observations || '');
    const [saving, setSaving] = useState(false);
    const flag = photo.ai_flag || 'ok';
    const flagMeta = flag === 'critical'
        ? { color: '#EF4444', bg: 'rgba(239,68,68,0.06)', label: 'CRÍTICO' }
        : flag === 'warning'
            ? { color: '#F59E0B', bg: 'rgba(245,158,11,0.06)', label: 'REVISAR' }
            : { color: '#00875A', bg: 'rgba(0,135,90,0.06)', label: 'OK' };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/inspector/photos/${photo.id}/observations`, { observations: text });
            toast.success('Observación guardada');
            setEditing(false);
            onEdited && onEdited();
        } catch (e) {
            toast.error(apiErrorMessage(e, 'No se pudo guardar'));
        } finally { setSaving(false); }
    };

    return (
        <li className="border-l-4 p-3 flex gap-3" style={{ borderColor: flagMeta.color, background: flagMeta.bg }}>
            {photo.photo_url ? (
                <img src={photo.photo_url} alt="evidencia" className="w-20 h-20 object-cover shrink-0 border"
                    style={{ borderColor: flagMeta.color }} />
            ) : (
                <div className="w-20 h-20 shrink-0 flex items-center justify-center opacity-30 border" style={{ borderColor: 'var(--border)' }}>
                    <Camera className="w-6 h-6" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5"
                        style={{ background: flagMeta.color, color: 'white' }}>{flagMeta.label}</span>
                </div>
                {photo.question && (
                    <div className="text-xs font-bold opacity-80 mb-0.5">{photo.question}</div>
                )}
                <div className="text-xs opacity-90 mb-1">
                    <strong>IA:</strong> {photo.ai_analysis || 'Sin análisis'}
                </div>
                {editing ? (
                    <div className="mt-2 space-y-1">
                        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
                            className="w-full px-2 py-1 border text-xs bg-transparent"
                            style={{ borderColor: 'var(--border)' }} />
                        <div className="flex gap-2">
                            <button onClick={save} disabled={saving} data-testid="save-observation-btn"
                                className="text-white px-2 py-1 text-xs font-bold" style={{ background: ACCENT }}>
                                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Guardar'}
                            </button>
                            <button onClick={() => { setEditing(false); setText(photo.observations || ''); }}
                                className="border px-2 py-1 text-xs font-bold" style={{ borderColor: 'var(--border)' }}>Cancelar</button>
                        </div>
                    </div>
                ) : (
                    <div className="text-xs mt-1">
                        <strong>Auditor:</strong> {photo.observations || <em className="opacity-60">Sin observaciones</em>}
                        {!readOnly && (
                            <button onClick={() => setEditing(true)}
                                className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold"
                                style={{ color: ACCENT }}>
                                <Pencil className="w-3 h-3" /> Editar
                            </button>
                        )}
                    </div>
                )}
            </div>
        </li>
    );
}

function Kpi({ label, value }) {
    return (
        <div className="p-2 border" style={{ borderColor: 'var(--border)' }}>
            <div className="text-[9px] uppercase tracking-widest font-bold opacity-60">{label}</div>
            <div className="text-sm font-display font-black truncate">{value}</div>
        </div>
    );
}
